import { PropsWithChildren } from 'react';
import { Typography, tokensRaw } from '@vp/swan';
import styled from 'styled-components';
import {
    FieldViewSkinContext,
    FieldViewSkinType,
    useFieldViewSkin,
} from '../../../context/FieldViewContext';
import { View as BooleanFieldView } from '../FormFields/BooleanField/BooleanField';
import { View as DateFieldView } from '../FormFields/DateField/DateField';
import { View as FilesFieldView } from '../FormFields/FilesField';
import { View as GridChoiceFieldView } from '../FormFields/GridChoiceField/GridChoiceField';
import { View as InputFieldView } from '../FormFields/InputField';
import { View as MultiChoiceFieldView } from '../FormFields/MultiChoiceField/MultiChoiceField';
import { View as MultiGridChoiceFieldView } from '../FormFields/MultiGridChoiceField/MultiGridChoiceField';
import { View as MultiInputFieldView } from '../FormFields/MultiInputField';
import { View as RadioChoiceFieldView } from '../FormFields/RadioChoiceField/RadioChoiceField';
import { View as SelectChoiceFieldView } from '../FormFields/SelectChoiceField/SelectChoiceField';
import { View as TextAreaFieldView } from '../FormFields/TextAreaField';
import { WorkEntityField } from '../FormFields/WorkEntityField';
import { FieldFragment } from '../brief.generated';

interface FieldDisplayProps {
    field: FieldFragment;
    colWidthOverride?: NonNullable<1 | 2 | 4 | 3 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12>;
}

export default function FieldDisplay({ field, colWidthOverride }: FieldDisplayProps) {
    switch (field.__typename) {
        case 'InputField':
            return <InputFieldView inputValue={field.inputValue} />;
        case 'TextAreaField':
            return <TextAreaFieldView textAreaValue={field.textAreaValue} />;
        case 'SelectChoiceField':
            return (
                <SelectChoiceFieldView options={field.options} choiceValue={field.choiceValue} />
            );
        case 'RadioChoiceField':
            return <RadioChoiceFieldView options={field.options} choiceValue={field.choiceValue} />;
        case 'MultiChoiceField':
            return (
                <MultiChoiceFieldView
                    options={field.options}
                    multiChoiceValue={field.multiChoiceValue}
                />
            );
        case 'MultiInputField':
            return <MultiInputFieldView multiInputValue={field.multiInputValue} />;
        case 'WorkEntityField':
            return <WorkEntityField {...field} />;
        case 'MultiGridChoiceField':
            return <MultiGridChoiceFieldView colWidthOverride={colWidthOverride} {...field} />;
        case 'FilesField':
            return <FilesFieldView {...field} />;
        case 'GridChoiceField':
            return <GridChoiceFieldView {...field} />;
        case 'DateField':
            return <DateFieldView dateValue={field.dateValue} />;
        case 'BooleanField':
            return <BooleanFieldView {...field} />;
        default:
            return <div>{field.__typename} not yet implemented</div>;
    }
}

export const FieldSet = styled.fieldset`
    border: none;
    margin: 0 ${tokensRaw.SwanSemSpace5} ${tokensRaw.SwanSemSpace7};
    padding: 0 0;
`;

interface FieldDisplayWrapperProps extends PropsWithChildren {
    title: string;
    skin?: FieldViewSkinType;
}

export function FieldDisplayWrapper(props: FieldDisplayWrapperProps) {
    const skin = useFieldViewSkin(props.skin);

    return (
        <FieldViewSkinContext.Provider value={skin}>
            <FieldSet>
                <Typography fontSize={skin.fontSize} fontWeight="bold" pb={'3'}>
                    {props.title}
                </Typography>
                {props.children}
            </FieldSet>
        </FieldViewSkinContext.Provider>
    );
}
